import HBIcon from "@/components/HBIcon";
import HBLoading from "@/components/HBLoading";
import HBPopover from "@/components/HBPopover";
import { LOCAL_STORAGE_KEY } from "@/constants/localStorageKey";
import useAppStore from "@/context/store";
import { checkPinExisted, createPin } from "@/services";
import { filterBoards } from "@/utils/filterBoards";
import { getContentType } from "@/utils/getContentType";
import sortBoardsByTitleAndCreateTime, {
  setBoardsTitlePinyin,
} from "@/utils/sortBoardsByTitleAndCreateTime";
import useRequest from "@/utils/useReq";
import { type PopoverProps } from "antd";
import classNames from "classnames";
import _ from "lodash";
import {
  ReactChild,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react";
import { Else, If, Then, When } from "react-if";
import HBSearchInput from "../../HBSearchInput";
import BoardList from "./BoardList";
import styles from "./index.module.less";

const LAST_BOARD_NUM = 5;

interface IProps {
  contentRef?: RefObject<HTMLDivElement>;
  pin: IPin;
  onOk?: (pin: IPin) => void;
}

export function PinDropdownView({ contentRef, pin, onOk }: IProps) {
  const pinId = pin?.pin_id;
  const [keyword, setKeyword] = useState("");
  const { lastBoards: userLastBoards, setLastBoards } = useAppStore(
    ({ lastBoards, setLastBoards }) => ({
      lastBoards,
      setLastBoards,
    })
  );
  const searchInputRef = useRef<{ input: HTMLInputElement }>(null);

  useEffect(() => {
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    // 在safari中，focus会导致页面滚动位置变化，所以不执行
    if (isSafari) {
      return;
    }

    searchInputRef.current?.input?.focus({ preventScroll: true });
  }, []);

  useMemo(async () => {
    if (userLastBoards?.length <= 0) {
      const userLastBoards = await useAppStore.getState().refreshLastBoards();
      if (userLastBoards) {
        try {
          window.localStorage.setItem(
            LOCAL_STORAGE_KEY.UPDATE_LAST_BOARDS,
            JSON.stringify(userLastBoards)
          );
        } catch (e) {
          console.error("设置更新最近采集失败");
        }
      }
    }
  }, [userLastBoards?.length]);

  const { data: boardData, loading: boardLoading } = useRequest(
    async () => {
      // const result = await getLastBoards({ extra: "cover" });
      // 将画板增加拼音
      let boardList = userLastBoards;
      if (boardList?.length > 0 && !boardList?.[0]?._pinyin) {
        boardList = await setBoardsTitlePinyin(boardList);
        setLastBoards(boardList);
        return;
      }
      // 过滤原创画板
      const boards =
        boardList?.filter((board) => !board?.extra?.is_creation) || [];
      // 为画板添加首字母与拼音
      const withPinyinBoards = boards.map((board) => {
        const item = { ...board };
        let firstLetter = `${
          item._pinyin[0]?.length > 0
            ? item._pinyin[0][0]
            : item._pinyin[0] || ""
        }`.toLocaleUpperCase();
        if (!/[A-Z]/.test(firstLetter)) {
          firstLetter = "#";
        }
        item._firstLetter = firstLetter;
        return item;
      });
      // 前5个画板作为历史画板
      const lastBoards = withPinyinBoards
        .slice(0, LAST_BOARD_NUM)
        .map((board, index) => {
          const item = {
            ...board,
          };
          if (index === 0) {
            item._icon = "time";
          }
          return item;
        });
      // 剩余画板按字母排序
      const otherBoards = Object.entries(
        _.groupBy(
          sortBoardsByTitleAndCreateTime(
            withPinyinBoards.slice(LAST_BOARD_NUM, withPinyinBoards.length)
          ),
          "_firstLetter"
        )
      )
        .sort((a, b) => `${a[0]}`.charCodeAt(0) - `${b[0]}`.charCodeAt(0))
        .reduce((result, [firstLetter, group]) => {
          result = result.concat(
            group.map((board, index) => {
              const item = { ...board };
              if (index === 0) {
                item._icon = firstLetter;
              }
              return item;
            })
          );
          return result;
        }, []);

      return {
        groupByLetterBoards: [...lastBoards, ...otherBoards],
        withPinyinBoards,
      };
    },
    {
      cacheKey: "pinCreatorBoards",
      refreshDeps: [userLastBoards],
    }
  );

  const onSelectBoard = useCallback(
    async (boardId) => {
      // const values = form.current?.getFieldsValue(true) || null;
      let result;
      if (pin?.pin_id) {
        result = await createPin({
          board_id: boardId,
          via: pin.pin_id,
          text: pin.raw_text,
          tags: pin.tags,
        });

        if (result && !result.err) {
          onOk && onOk(result.pin);
        }
      }
    },
    [onOk, pin]
  );

  const onKeywordChange = useCallback((evt) => {
    const keyword = evt.target.value;
    setKeyword(keyword.slice(0, 32));
  }, []);

  const { data: existPin } = useRequest(
    async () => {
      if (!pinId) {
        return null;
      }

      const result: any = await checkPinExisted({
        pin_id: pinId,
      });
      return result?.exist_pin;
    },
    {
      refreshDeps: [pinId],
    }
  );

  const groupByLetterBoards = useMemo(() => {
    return boardData?.groupByLetterBoards || [];
  }, [boardData]);

  const searchBoards = useMemo(() => {
    let boards = filterBoards(keyword, boardData);

    return boards.map((board, index) => {
      // if (index === 0) {
      //   item._icon = "search";
      // }

      return { ...board };
    });
  }, [boardData, keyword]);

  return (
    <div
      className={classNames([styles.board, existPin ? styles.existPin : null])}
      data-content-type={getContentType(pin)}
      ref={contentRef}
    >
      <div className={styles.search}>
        <HBSearchInput
          value={keyword}
          wrapClassName={styles.searchInputWrap}
          onInput={onKeywordChange}
          placeholder="搜索画板"
          className={styles.searchInput}
          hideSearchBtn
          ref={searchInputRef}
        />
      </div>
      <When condition={existPin}>
        <div className={styles.warning}>
          <HBIcon className={styles.icon} type="ic_Warning" />
          <div className={styles.text}>该图已在画板</div>
          <a
            className={styles.link}
            href={`/boards/${existPin?.board?.board_id}`}
            target="_blank"
          >
            {existPin?.board?.title}
          </a>
        </div>
      </When>
      <If condition={boardLoading && !boardData}>
        <Then>
          <div className={styles.loading}>
            <HBLoading size="24px" />
          </div>
        </Then>
        <Else>
          <BoardList
            dataSource={keyword ? searchBoards : groupByLetterBoards}
            keyword={keyword}
            onSelectBoard={onSelectBoard}
          />
        </Else>
      </If>
    </div>
  );
}
export function PinDropdown({
  children,
  visible,
  placement,
  popoverProps = {},
  ...props
}: IProps & {
  children: ReactChild;
  visible: boolean;
  popoverProps?: PopoverProps;
  placement: PopoverProps["placement"];
}) {
  return (
    <HBPopover
      content={<PinDropdownView {...props} />}
      overlayClassName={styles.dropdown}
      overlayStyle={{ minWidth: 340 }}
      arrowContent={null}
      placement={placement || "bottomLeft"}
      visible={visible}
      {...popoverProps}
      align={
        placement === "topLeft" ? { offset: [-10, 4] } : { offset: [-10, 0] }
      }
      autoAdjustOverflow
      destroyTooltipOnHide
    >
      {children}
    </HBPopover>
  );
}
